/**
 * leaveGuard — ask before a page with unsaved changes is left.
 *
 * A page calls useLeaveGuard(dirty, message). While `dirty`:
 *   - closing or reloading the tab, or typing another URL, gets the browser's
 *     own "Leave site?" prompt (beforeunload);
 *   - the side menu and the Logout item go through confirmLeave(go), which
 *     asks first;
 *   - Back lands on a copy of this page's history entry first, so it can be
 *     asked about too (the router has no blocker outside a data router).
 */
import { useEffect } from 'react';
import { Modal } from 'antd';

let active = null; // the message of the page that has unsaved changes

function ask(message, onLeave, onStay) {
  Modal.confirm({
    title: 'Leave without saving?',
    content: message,
    okText: 'Leave',
    okButtonProps: { danger: true },
    cancelText: 'Stay',
    onOk: onLeave,
    onCancel: onStay,
  });
}

/** Run `go` now, or once the user agrees to drop the unsaved changes. */
export function confirmLeave(go) {
  if (!active) {
    go();
    return;
  }
  ask(active, () => {
    active = null;
    go();
  });
}

export function useLeaveGuard(dirty, message) {
  useEffect(() => {
    if (!dirty) {return undefined;}
    active = message;
    const path = window.location.pathname;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    let asking = false;
    const onPop = () => {
      if (asking || active !== message || window.location.pathname !== path) {return;}
      // Back took the copy off; put it back and ask.
      window.history.pushState(window.history.state, '', window.location.href);
      asking = true;
      ask(message, () => {
        active = null;
        window.removeEventListener('popstate', onPop);
        window.history.go(-2);
      }, () => { asking = false; });
    };
    window.history.pushState(window.history.state, '', window.location.href);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPop);
      if (active === message) {active = null;}
      // Saved or discarded on this page: drop the copy again, so Back is one step.
      if (window.location.pathname === path) {window.history.back();}
    };
  }, [dirty, message]);
}
